import { LeftOutlined } from "@ant-design/icons";
import { Col, Form, Row } from "antd";
import Button from "components/button/Button";
import InputComponent from "components/formInput/Input";
import InputPassword from "components/formInput/InputPassword";
import Loading from "components/loading/Loading";
import PopupConfirm from "components/popup-confirm";
import { useFormik } from "formik";
import {
  apiDeleteClinicById,
  apiGetClinic,
  apiGetPostCodeInfo,
  apiUpdateClinic,
} from "helpers/api/clinic";
import { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { ROUTE } from "routes/route";
import { showToastError, showToastSuccess } from "utils";
import { validationSchemaEdit } from "./schema";
import "./styles.scss";

const DEFAULT_VALUE = {
  address: {},
  number_hidden: 0,
  number_display: 0,
};

const AdminClinicEditPage = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clinicInfo, setClinicInfo] = useState(null);

  const initialValues = useMemo(() => {
    if (clinicInfo) {
      const number_display = clinicInfo.number_display || 0;
      const number_hidden = clinicInfo.number_hidden || 0;
      return {
        ...DEFAULT_VALUE,
        email: clinicInfo.email,
        clinic_name: clinicInfo.clinic_name,
        clinic_name_furigana: clinicInfo.clinic_name_furigana,
        address: clinicInfo.address,
        email: clinicInfo.email,
        confirmEmail: clinicInfo.email,
        phone_number: clinicInfo.phone_number,
        decode_password: clinicInfo.decode_password,
        confirmPassword: clinicInfo.decode_password,
        number_hidden,
        number_display,
      };
    }

    return DEFAULT_VALUE;
  }, [clinicInfo]);

  useEffect(() => {
    setIsLoading(true);
    apiGetClinic(id)
      .then((res) => {
        setIsLoading(false);
        if (res?.data) {
          setClinicInfo(res.data);
        }
      })
      .catch(({ error }) => {
        showToastError(`messages.${error.reason}`);
        setIsLoading(false);
      });
  }, []);

  const onSubmit = async () => {
    setIsLoading(true);
    const res = await apiUpdateClinic(id, {
      email: values.email,
      password: values.decode_password,
      clinic_name: values.clinic_name,
      clinic_name_furigana: values.clinic_name_furigana,
      address: values.address,
      email: values.email,
      phone_number: values.phone_number,
    });
    if (res.data) {
      showToastSuccess(`messages.MSG-USP055`);
    } else if (res?.error?.reason) {
      showToastError(`messages.${res?.error?.reason}`);
    }
    setIsLoading(false);
  };

  const { values, errors, handleChange, setFieldValue, handleSubmit, dirty } =
    useFormik({
      validateOnChange: false,
      initialValues,
      validationSchema: validationSchemaEdit(t),
      onSubmit,
      enableReinitialize: true,
    });
  const showModal = () => {
    setIsOpenDelete(true);
  };

  const handleDelete = () => {
    setIsLoading(true);
    apiDeleteClinicById({
      clinic_ids: [id],
    }).then((res) => {
      if (res.error) {
        setIsLoading(false);
        setIsOpenDelete(false);
        showToastError("messages.MSG-USP071");
      }
      if (res.data) {
        showToastSuccess("messages.MSG-USP070");
        setIsLoading(false);
        history.push(ROUTE.ADMIN.CLINIC);
      }
    })
      .catch(({ error }) => {
        setIsLoading(false);
        showToastError(`messages.${error.reason}`);
      });
  };

  const handleCancel = () => {
    setIsOpenDelete(false);
  };

  const handleChangePostCode = () => {
    const postCode = `${values?.address?.postal_code}`.replaceAll("-", "");
    setFieldValue("address.province", "");
    setFieldValue("address.name", ``);
    apiGetPostCodeInfo(postCode, (address) => {
      setFieldValue("address.province", address?.prefecture);
      setFieldValue("address.name", `${address?.city}${address?.area}`);
    });
  };

  const checkRequired =
    !values.clinic_name ||
    !values.clinic_name_furigana ||
    !values.address.postal_code ||
    !values.address.province ||
    !values.address.name ||
    !values.address.building ||
    !values.phone_number ||
    !values.email ||
    !values.confirmEmail ||
    !values.decode_password ||
    !values.confirmPassword;

  return (
    <div className="main-content admin-clinic-edit-container">
      <div className="root-right-header">
        <div className="screen-name">{t("admin_role")}</div>
      </div>
      <div className="nav screen-title-sub">
        <span
          onClick={() => history.push(ROUTE.ADMIN.CLINIC)}
          style={{ cursor: "pointer" }}
        >
          <LeftOutlined />
        </span>
        {t("adminClinicsEdit.title", { name: clinicInfo?.clinic_name })}
      </div>

      <Form autoComplete="off" style={{ width: "100%" }} onFinish={handleSubmit}>
        <Container>
          <Row gutter={[24, 24]} justify={"space-between"} className="sign-up-form">
            <Col xl={12} lg={12} md={24}>
              <InputComponent
                title={t("adminClinicsEdit.nameFullWidth")}
                placeholder={t("adminClinicsEdit.nameFullPlaceholder")}
                name="clinic_name"
                value={values.clinic_name}
                error={errors?.clinic_name}
                onChange={handleChange}
              />
              <InputComponent
                title={t("adminClinicsEdit.nameFurigana")}
                placeholder={t("adminClinicsEdit.nameFuriPlaceholder")}
                name="clinic_name_furigana"
                value={values.clinic_name_furigana}
                error={errors?.clinic_name_furigana}
                onChange={handleChange}
              />

              <div className="address mt-1">
                <div className="input-title">
                  {t("adminClinicsEdit.address")}
                </div>
                <div className="clinic-row">
                  <span className="post-code-icon">
                    {t("adminClinicsEdit.addressSymbol")}
                  </span>
                  <InputComponent
                    name="address.postal_code"
                    value={values?.address?.postal_code}
                    error={errors?.address?.postal_code}
                    onChange={handleChange}
                  />
                  <div className="search-button" onClick={handleChangePostCode}>
                    {t("adminClinicsEdit.addressSearch")}
                  </div>
                </div>
                <InputComponent
                  placeholder={t("adminClinicsEdit.provincePlaceholder")}
                  name="address.province"
                  value={values.address?.province}
                  error={errors.address?.province}
                  onChange={handleChange}
                />
                <InputComponent
                  placeholder={t("adminClinicsEdit.address1Placeholder")}
                  name="address.name"
                  value={values.address?.name}
                  error={errors.address?.name}
                  onChange={handleChange}
                />
                <InputComponent
                  placeholder={t("adminClinicsEdit.address2Placeholder")}
                  name="address.building"
                  value={values.address?.building}
                  error={errors.address?.building}
                  onChange={handleChange}
                />
              </div>

              <InputComponent
                title={t("adminClinicsEdit.phone")}
                name="phone_number"
                type="numberString"
                // pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                value={values.phone_number}
                error={errors.phone_number}
                onChange={handleChange}
              />
            </Col>

            <Col xl={12} lg={12} md={24}>
              <div className="email-section">
                <InputComponent
                  title={t("adminClinicsEdit.email")}
                  placeholder="sanwa.ped@sanwaclinic.jp"
                  name="email"
                  value={values.email}
                  error={errors.email}
                  onChange={handleChange}
                  className="input-right"
                />
                <InputComponent
                  placeholder="sanwa.ped@sanwaclinic.jp"
                  des={t("adminClinicsEdit.forConfirm")}
                  name="confirmEmail"
                  value={values.confirmEmail}
                  error={errors.confirmEmail}
                  onChange={handleChange}
                  className="input-right"
                />
              </div>
              <div className="password-section">
                <InputPassword
                  className=" input-right"
                  title={t("adminClinicsEdit.password")}
                  type="password"
                  name="decode_password"
                  value={values.decode_password}
                  error={errors.decode_password}
                  onChange={handleChange}
                // disabled
                />
                <InputPassword
                  des={t("adminClinicsEdit.forConfirm")}
                  type="password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  error={errors.confirmPassword}
                  onChange={handleChange}
                  className="input-right"
                // disabled
                />
              </div>
              <div className="user-registered mt-1 input-title">
                {t("adminClinicsEdit.numberResiger", {
                  number:
                    initialValues.number_hidden + initialValues.number_display,
                })}
              </div>
              <Row className="user-registered">
                <span className=" mx-1">
                  {t("adminClinicsEdit.numberHidden", {
                    number: initialValues.number_hidden,
                  })}
                </span>
                <span className=" mx-1">
                  {t("adminClinicsEdit.numberShow", {
                    number: initialValues.number_display,
                  })}
                </span>
              </Row>

              <Button
                className="mt-1 submit-button"
                text={t("adminClinicsEdit.edit")}
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  borderRadius: 60,
                  marginTop: 150,
                }}
                height={40}
                onClick={handleSubmit}
                disabled={checkRequired}
              />
              <Button
                className="mt-1 delete-btn submit-button"
                text={t("adminClinicsEdit.delete")}
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  borderRadius: 60,
                  marginTop: 150,
                }}
                height={40}
                onClick={showModal}
              />
              <PopupConfirm
                open={isOpenDelete}
                onCancelText={t("adminClinicsEdit.modalDelete.cancel")}
                onOkText={t("adminClinicsEdit.modalDelete.delete")}
                content={t("adminClinicsEdit.modalDelete.content", {
                  clinic_name: clinicInfo?.clinic_name,
                })}
                onOk={handleDelete}
                onCancel={handleCancel}
                onOkColor="#FF3131"
              />

              {isLoading && <Loading />}
            </Col>
          </Row>
        </Container>
      </Form>
    </div>
  );
};

export default AdminClinicEditPage;
